/**
 * useNursingSummaryWebSocket Hook
 * Custom React hook for managing WebSocket connection to nursing summary service
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { hostConfig } from '../config';
import {
    WebSocketMessage,
    UseWebSocketReturn,
} from '../types/nursing-summary.types';

export const useNursingSummaryWebSocket = (): UseWebSocketReturn => {
    const [messages, setMessages] = useState<WebSocketMessage[]>([]);
    const [lastMessage, setLastMessage] = useState<WebSocketMessage | null>(null);
    const [connectionStatus, setConnectionStatus] = useState<
        'connecting' | 'connected' | 'disconnected' | 'error'
    >('disconnected');

    const wsRef = useRef<WebSocket | null>(null);
    const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const sessionIdRef = useRef<string | null>(null);

    /**
     * Get WebSocket URL based on environment
     */
    const getWebSocketUrl = useCallback((sessionId: string): string => {
        // Convert HTTP URL to WebSocket URL
        const apiUrl = hostConfig.API_URL;
        let wsUrl: string;

        if (apiUrl.startsWith('https://')) {
            wsUrl = apiUrl.replace('https://', 'wss://');
        } else if (apiUrl.startsWith('http://')) {
            wsUrl = apiUrl.replace('http://', 'ws://');
        } else {
            // Fallback - assume local development
            wsUrl = 'ws://localhost:5025/api/v1/';
        }

        // Remove trailing slash and add WebSocket endpoint
        wsUrl = wsUrl.replace(/\/$/, '');
        return `${wsUrl}/nursing-summary/ws/${sessionId}`;
    }, []);

    /**
     * Connect to WebSocket
     */
    const connect = useCallback((sessionId: string) => {
        if (!sessionId) {
            console.error('Session ID is required to connect to WebSocket');
            return;
        }

        sessionIdRef.current = sessionId;

        // Close existing connection if any
        if (wsRef.current) {
            wsRef.current.close();
        }

        try {
            const wsUrl = getWebSocketUrl(sessionId);
            console.log('Connecting to WebSocket:', wsUrl);

            setConnectionStatus('connecting');
            const ws = new WebSocket(wsUrl);

            ws.onopen = () => {
                console.log('WebSocket connected');
                setConnectionStatus('connected');

                // Clear messages from previous session
                setMessages([]);
                setLastMessage(null);
            };

            ws.onmessage = (event) => {
                try {
                    const message: WebSocketMessage = JSON.parse(event.data);
                    console.log('WebSocket message received:', message);

                    setLastMessage(message);
                    setMessages((prev) => [...prev, message]);

                    // Auto-close on completed or error
                    if (message.status === 'completed' || message.status === 'error') {
                        setTimeout(() => {
                            ws.close();
                        }, 1000);
                    }
                } catch (error) {
                    console.error('Error parsing WebSocket message:', error);
                }
            };

            ws.onerror = (error) => {
                console.error('WebSocket error:', error);
                setConnectionStatus('error');
            };

            ws.onclose = (event) => {
                console.log('WebSocket closed:', event.code, event.reason);
                setConnectionStatus('disconnected');
                wsRef.current = null;

                // Don't auto-reconnect on normal closure
                if (event.code !== 1000 && event.code !== 1001) {
                    // Abnormal closure - attempt reconnect after delay
                    if (sessionIdRef.current && reconnectTimeoutRef.current === null) {
                        console.log('Attempting to reconnect in 3 seconds...');
                        reconnectTimeoutRef.current = setTimeout(() => {
                            if (sessionIdRef.current) {
                                connect(sessionIdRef.current);
                            }
                            reconnectTimeoutRef.current = null;
                        }, 3000);
                    }
                }
            };

            wsRef.current = ws;
        } catch (error) {
            console.error('Failed to create WebSocket connection:', error);
            setConnectionStatus('error');
        }
    }, [getWebSocketUrl]);

    /**
     * Disconnect from WebSocket
     */
    const disconnect = useCallback(() => {
        if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
            reconnectTimeoutRef.current = null;
        }

        if (wsRef.current) {
            wsRef.current.close(1000, 'User disconnected');
            wsRef.current = null;
        }

        sessionIdRef.current = null;
        setConnectionStatus('disconnected');
    }, []);

    /**
     * Cleanup on unmount
     */
    useEffect(() => {
        return () => {
            disconnect();
        };
    }, [disconnect]);

    return {
        messages,
        lastMessage,
        connectionStatus,
        connect,
        disconnect,
    };
};
